import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import type { Message, ChatWidgetProps, ChatState } from "../types";
import { CHAT_CONFIG } from "../config";
import {
  generateSessionId,
  sanitizeResponseText,
  createAxiosConfig,
  convertHistoryMessages,
} from "../utils";
import { store } from "../../../redux/stores";

export const useChat = ({
  chatWigetUrl,
  email,
  curso_id,
  alias,
  defaultUserAvatar,
  defaultBotAvatar,
  welcomeMessage = "¡Hola! ¿En qué puedo ayudarte hoy?",
  unitData
}: ChatWidgetProps) => {
  const [state, setState] = useState<ChatState>({
    messages: [],
    isTyping: false,
    nickname: alias || "",
    isEditingNickname: false,
    tempNickname: "",
    sessionId: generateSessionId(),
    isLoadingHistory: false,
    historyOffset: 0,
    hasMoreHistory: true,
    historyLoaded: false,
    historyError: null,
    historyDisabled: false,
  });
  const authState = store.getState().auth;
  const bearerToken = authState.user.token;
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Load chat history
  const loadChatHistory = useCallback(
    async (offset = 0): Promise<Message[]> => {
      if (state.isLoadingHistory || state.historyDisabled) return [];

      setState((prev) => ({
        ...prev,
        isLoadingHistory: true,
        historyError: null,
      }));

      try {
        const payload = {
          email,
          curso_id,
          last_unit: unitData?.code,
          last_unit_name: unitData?.name,
          limit: CHAT_CONFIG.historyLimit,
          offset,
        };

        console.log("Loading history with payload:", payload, chatWigetUrl);

        const response = await axios.post(
          chatWigetUrl?.chathistory,
          payload,
          createAxiosConfig(bearerToken)
        );
        console.log("History API Response:", response.data);

        if (Array.isArray(response.data)) {
          const historyMessages = convertHistoryMessages(
            response.data.reverse(),
            offset,
            defaultBotAvatar,
            defaultUserAvatar
          );

          // Update offset and hasMoreHistory
          setState((prev) => ({
            ...prev,
            historyOffset: offset + response.data.length,
            hasMoreHistory: response.data.length >= CHAT_CONFIG.historyLimit,
          }));

          // CARGA INICIAL (offset === 0): Reemplazar mensajes y marcar como cargado
          if (offset === 0) {
            setState((prev) => ({
              ...prev,
              messages: historyMessages, // Historial en orden cronológico (más antiguo primero)
              historyLoaded: true,
            }));
          }

          return historyMessages;
        } else {
          console.warn("Unexpected response format:", response.data);
          if (offset === 0) {
            setState((prev) => ({ ...prev, historyLoaded: true }));
          }
          return [];
        }
      } catch (error) {
        console.log("History loading failed silently:", error);

        if (axios.isAxiosError(error) && error.request) {
          setState((prev) => ({ ...prev, historyDisabled: true }));
        }

        if (offset === 0) {
          setState((prev) => ({ ...prev, historyLoaded: true }));
        }

        return [];
      } finally {
        setState((prev) => ({ ...prev, isLoadingHistory: false }));
      }
    },
    [
      email,
      curso_id,
      defaultBotAvatar,
      defaultUserAvatar,
      state.isLoadingHistory,
      state.historyDisabled,
      unitData?.code
    ]
  );

  // Send message
  const sendMessage = useCallback(
    async (messageText: string) => {
      if (!messageText.trim()) return;

      const userMessage: Message = {
        id: Date.now().toString(),
        text: messageText,
        sender: "user",
        timestamp: new Date(),
        avatar: defaultUserAvatar,
      };

      setState((prev) => ({
        ...prev,
        messages: [...prev.messages, userMessage],
        isTyping: true,
      }));

      try {
        const payload = {
          email,
          curso_id,
          alias: state.nickname || alias,
          last_unit: unitData?.code,
          last_unit_name: unitData?.name,
          chatInput: messageText,
          sessionId: state.sessionId,
        };

        console.log("Sending message payload:", payload);

        const response = await axios.post(
          chatWigetUrl?.chatendpoint,
          payload,
          createAxiosConfig(bearerToken)
        );
        console.log("Chat API Response:", response.data);

        let botResponseText = "Lo siento, no pude procesar tu mensaje.";

        if (
          Array.isArray(response.data) &&
          response.data.length > 0 &&
          response.data[0].output
        ) {
          botResponseText = sanitizeResponseText(response.data[0].output);
        }

        const botMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: botResponseText,
          sender: "bot",
          timestamp: new Date(),
          avatar: defaultBotAvatar,
        };

        setState((prev) => ({
          ...prev,
          messages: [...prev.messages, botMessage],
          isTyping: false,
        }));
      } catch (error) {
        console.log("Message sending failed:", error);

        let errorMessage = "Lo siento, hubo un problema con la conexión.";

        if (axios.isAxiosError(error)) {
          if (error.code === "ECONNABORTED") {
            errorMessage =
              "Timeout - El servidor tardó demasiado en responder.";
          } else if (error.response) {
            errorMessage = `Error del servidor: ${error.response.status} - ${error.response.statusText}`;
          } else if (error.request) {
            errorMessage =
              "Error de conexión. Verifica tu conexión a internet.";
          } else {
            errorMessage = `Error de configuración: ${error.message}`;
          }
        } else if (error instanceof Error) {
          errorMessage = `Error: ${error.message}`;
        }

        const botErrorMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: errorMessage,
          sender: "bot",
          timestamp: new Date(),
          avatar: defaultBotAvatar,
        };

        setState((prev) => ({
          ...prev,
          messages: [...prev.messages, botErrorMessage],
          isTyping: false,
        }));
      }
    },
    [
      email,
      curso_id,
      alias,
      state.nickname,
      state.sessionId,
      defaultUserAvatar,
      defaultBotAvatar,
      unitData?.code
    ]
  );

  // Handle scroll for infinite loading (scroll hacia arriba)
  const handleScroll = useCallback(() => {
    if (
      !messagesContainerRef.current ||
      state.isLoadingHistory ||
      !state.hasMoreHistory ||
      state.historyError ||
      state.historyDisabled
    )
      return;

    const container = messagesContainerRef.current;

    // Si el usuario hace scroll hacia arriba y llega al límite
    if (container.scrollTop <= CHAT_CONFIG.scrollThreshold) {
      // Guardar altura actual para mantener posición
      const currentScrollHeight = container.scrollHeight;
      const currentScrollTop = container.scrollTop;

      loadChatHistory(state.historyOffset)
        .then((historyMessages) => {
          if (historyMessages.length > 0) {
            // Agregar historial más antiguo ANTES del historial existente
            setState((prev) => ({
              ...prev,
              messages: [...historyMessages, ...prev.messages],
            }));

            // Mantener posición de scroll después de agregar mensajes arriba
            setTimeout(() => {
              if (messagesContainerRef.current) {
                const newScrollHeight =
                  messagesContainerRef.current.scrollHeight;
                const scrollDiff = newScrollHeight - currentScrollHeight;
                messagesContainerRef.current.scrollTop =
                  currentScrollTop + scrollDiff;
              }
            }, 50);
          }
        })
        .catch(() => {
          console.log("Infinite scroll history loading failed silently");
        });
    }
  }, [
    loadChatHistory,
    state.historyOffset,
    state.isLoadingHistory,
    state.hasMoreHistory,
    state.historyError,
    state.historyDisabled,
  ]);

  // Initialize welcome message and load initial history
  useEffect(() => {
    if (state.messages.length === 0 && !state.historyLoaded) {
      // Mostrar mensaje de bienvenida temporalmente
      const welcomeMsg: Message = {
        id: "welcome",
        text: welcomeMessage,
        sender: "bot",
        timestamp: new Date(),
      };
      setState((prev) => ({ ...prev, messages: [welcomeMsg] }));

      // Cargar historial inicial
      if (!state.historyDisabled) {
        loadChatHistory(0).catch(() => {
          // Si falla, mantener mensaje de bienvenida
          console.log("Initial history load failed, keeping welcome message");
        });
      } else {
        setState((prev) => ({ ...prev, historyLoaded: true }));
      }
    }
  }, [
    state.messages.length,
    state.historyLoaded,
    welcomeMessage,
    state.historyDisabled,
    loadChatHistory,
  ]);

  return {
    state,
    setState,
    messagesEndRef,
    messagesContainerRef,
    sendMessage,
    handleScroll,
    loadChatHistory,
  };
};
